/* eslint-disable no-nested-ternary */
import { useState, useEffect } from "react";
import styles from "./deposit.module.scss";
import { useAppDispatch, useAppSelector } from "src/page/store";
import {
  Autocomplete,
  Button,
  ButtonGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormControl,
  CircularProgress
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import TransactionWelCard from "src/components/transactionWelCard/TransactionWelCard";
import { Users } from "../users/usersSlice";
import { Wallet } from "../wallets/walletSlice";
import { Group } from "../group/groupSlice";
import { GroupWallet } from "../groupDetail/groupDetailSlice";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  addFundsAction,
  fetchUserListAction,
  fetchWalletListAction,
  resetwalletListAction,
  fetchWalletByIdAction,
  fetchGroupListAction,
  fetchGroupDetailAction,
  adjustFundsAction
} from "./depositSlice";
import LoadingModal from "src/components/UI/loadingModal/LoadingModal";
import { EMPTY_STRING, PARTICIPANT, roleMapping } from "src/components/constant/constant";
import { useParams } from "react-router-dom";

export default function Deposit() {
  const dispatch = useAppDispatch();
  const {
    users,
    wallets,
    selectedWallet: fetchedWallet,
    groups,
    groupDetail,
    addFundsLoading,
    groupsLoading,
    groupDetailLoading,
    walletLoading,
    userLoading
  } = useAppSelector((state) => state.deposit);

  const [amount, setAmount] = useState<number>(0);
  const [selectedParticipant, setSelectedParticipant] = useState<Users | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [selectedGroupWallet, setSelectedGroupWallet] = useState<GroupWallet | null>(null);
  const [fundAction, setFundAction] = useState<string>("DEPOSIT");
  const [selectedOption, setSelectedOption] = useState<string>(EMPTY_STRING);
  const [balance, setBalance] = useState<number>(0);
  const [depositMode, setDepositMode] = useState<"Individual" | "Group">("Individual");
  const [error, setError] = useState({
    participant: EMPTY_STRING,
    wallet: EMPTY_STRING,
    group: EMPTY_STRING,
    groupWallet: EMPTY_STRING,
    option: EMPTY_STRING,
    amount: EMPTY_STRING
  });

  const { walletId } = useParams();

  useEffect(() => {
    if (!walletId) {
      dispatch(fetchUserListAction({ size: 200, from: 0, user_filter: { role: PARTICIPANT } }));
      dispatch(fetchGroupListAction({ size: 100, from: 0, filter: {} }));
    }
  }, [dispatch, walletId]);

  useEffect(() => {
    if (walletId) {
      dispatch(fetchWalletByIdAction(walletId));
    }
  }, [walletId, dispatch]);

  useEffect(() => {
    if (fetchedWallet) {
      setSelectedWallet(fetchedWallet);
      setBalance(fetchedWallet.balance);
      setFundAction("DEPOSIT");
      setSelectedOption("DEPOSIT");

      if (fetchedWallet?.participant) {
        const participant: Users = {
          id: fetchedWallet?.participant.display_id,
          initial: EMPTY_STRING,
          fullName: fetchedWallet?.participant.fullName,
          firstName: fetchedWallet?.participant.firstName,
          lastName: fetchedWallet?.participant.lastname,
          dob: EMPTY_STRING,
          email: fetchedWallet?.participant.email,
          gender: EMPTY_STRING,
          phone: fetchedWallet?.participant.phone,
          line1: EMPTY_STRING,
          line2: EMPTY_STRING,
          city: EMPTY_STRING,
          state: EMPTY_STRING,
          postCode: EMPTY_STRING,
          Country: EMPTY_STRING,
          role: [
            {
              organisation_id: EMPTY_STRING,
              value: fetchedWallet?.participant.role
            }
          ],
          currentOrganisationRole: fetchedWallet?.participant.role,
          active: true,
          lastActive: EMPTY_STRING,
          userId: String(fetchedWallet?.participant.id),
          createdAt: EMPTY_STRING
        };
        setSelectedParticipant(participant);
      }
    }
  }, [fetchedWallet]);

  useEffect(() => {
    if (!walletId) {
      setSelectedWallet(null);
      setAmount(0);
      clearError();

      if (depositMode === "Individual" && selectedParticipant) {
        dispatch(fetchWalletListAction(String(selectedParticipant?.userId)));
      } else if (depositMode === "Group" && selectedGroup) {
        dispatch(fetchGroupDetailAction(selectedGroup?.group_id));
      } else {
        dispatch(resetwalletListAction({ list: [] }));
      }
    }
  }, [selectedParticipant, selectedGroup, depositMode, walletId, dispatch]);

  useEffect(() => {
    if (selectedWallet) {
      const walletBalance = wallets?.list.find((wallet: Wallet) => wallet?.id === selectedWallet?.id);

      if (walletBalance) {
        setBalance(walletBalance?.balance);
      }
    }
  }, [selectedWallet, wallets?.list]);

  useEffect(() => {
    if (selectedGroupWallet) {
      const walletBalance = groupDetail?.wallets.find(
        (wallet: GroupWallet) => wallet?.wallet_id === selectedGroupWallet?.wallet_id
      );

      if (walletBalance) {
        setBalance(walletBalance?.balance);
      }
    }
  }, [groupDetail?.wallets, selectedGroupWallet]);

  useEffect(() => {
    setSelectedOption(EMPTY_STRING);
  }, [fundAction]);

  const resetFormFields = () => {
    setAmount(0);
    setSelectedOption(EMPTY_STRING);
    clearError();

    if (depositMode === "Individual" && selectedParticipant) {
      dispatch(fetchWalletListAction(String(selectedParticipant?.userId)));
    } else if (depositMode === "Group" && selectedGroup) {
      dispatch(fetchGroupDetailAction(selectedGroup?.group_id));
    }
  };

  const clearError = () => {
    setError({
      participant: EMPTY_STRING,
      wallet: EMPTY_STRING,
      group: EMPTY_STRING,
      groupWallet: EMPTY_STRING,
      option: EMPTY_STRING,
      amount: EMPTY_STRING
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      participant: EMPTY_STRING,
      wallet: EMPTY_STRING,
      group: EMPTY_STRING,
      groupWallet: EMPTY_STRING,
      option: EMPTY_STRING,
      amount: EMPTY_STRING
    };

    if (depositMode === "Individual") {
      if (!selectedParticipant) {
        newErrors.participant = "Please select a participant";
        isValid = false;
      }

      if (!selectedWallet) {
        newErrors.wallet = "Please select a wallet";
        isValid = false;
      }
    } else {
      if (!selectedGroup) {
        newErrors.group = "Please select a group";
        isValid = false;
      }

      if (!selectedGroupWallet) {
        newErrors.groupWallet = "Please select a wallet";
        isValid = false;
      }
    }

    if (!selectedOption) {
      newErrors.option = "Please select a payment reason";
      isValid = false;
    }

    if (amount <= 0) {
      newErrors.amount = "Please enter a valid amount";
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  const handleTransaction = () => {
    if (!validateForm()) return;

    const walletId = depositMode === "Individual" ? String(selectedWallet?.id) : String(selectedGroupWallet?.wallet_id);

    if (selectedOption === "DEPOSIT") {
      dispatch(
        addFundsAction(
          {
            wallet_id: walletId,
            amount: amount,
            remark: selectedOption
          },
          () => {
            resetFormFields();
          }
        )
      );
    } else {
      dispatch(
        adjustFundsAction(
          {
            wallet_id: walletId,
            amount: amount * (fundAction === "DEPOSIT" ? 1 : -1),
            remark: selectedOption
          },
          () => {
            resetFormFields();
          }
        )
      );
    }
  };

  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    setSelectedOption(event.target.value);
    setError({ ...error, option: EMPTY_STRING });
  };

  const handleFundActionChange = (event: SelectChangeEvent<string>) => {
    setFundAction(event.target.value);
    setSelectedOption(EMPTY_STRING);
  };

  const handleModeChange = (mode: "Individual" | "Group") => {
    setDepositMode(mode);
    setSelectedWallet(null);
    setSelectedGroupWallet(null);
    setSelectedOption(EMPTY_STRING);
    setBalance(0);
    clearError();
  };

  const addPaymentTypes = [
    { id: "DEPOSIT", title: "Deposit" },
    { id: "CREDIT", title: "Credit - Balance Adjust" },
    { id: "CREDIT_MISSED_SEGTTLE_TRANSACTION", title: "Credit missed settled transaction" },
    { id: "BONUS", title: "Bonus/Reward" },
    { id: "OTHER", title: "Settled transaction missed" }
  ];

  const removePaymentTypes = [
    { id: "WITHDRAWAL", title: "Withdrawal" },
    { id: "DEBIT", title: "Debit balance adjust" },
    { id: "DEBIT_MISSED_SETTLEMENT_TRANSACTION", title: "Debit Missed settlement transaction" }
  ];

  return (
    <div className={styles.depositwrappper} aria-label="Deposit Money">
      {addFundsLoading && <LoadingModal />}
      <div className={styles.WelCard}>
        <TransactionWelCard heading={"Deposit Money"} subHeading={"Deposit and Revoke money from wallet"} />
      </div>
      <div className={styles.depositContainer}>
        <div className={styles.container}>
          <ButtonGroup variant="outlined" aria-label="deposit mode selection" className={styles.actionTypeBtnGroup}>
            <Button
              style={{
                backgroundColor: depositMode === "Individual" ? "#1e293b" : "#fff",
                border: "1px solid #1e293b",
                color: depositMode === "Individual" ? "#FFFFFF" : "#1e293b"
              }}
              onClick={() => handleModeChange("Individual")}
              disabled={Boolean(walletId)}
              aria-label="Individual Deposit"
            >
              Individual Deposit
            </Button>
            <Button
              style={{
                backgroundColor: depositMode === "Group" ? "#1e293b" : "#fff",
                border: "1px solid #1e293b",
                color: depositMode === "Group" ? "#FFFFFF" : "#1e293b"
              }}
              onClick={() => handleModeChange("Group")}
              disabled={Boolean(walletId)}
              aria-label="Group Deposit"
            >
              Group Deposit
            </Button>
          </ButtonGroup>
          {depositMode === "Individual" ? (
            <>
              <Autocomplete
                options={users?.list}
                getOptionLabel={(option) => option?.firstName + " " + option?.lastName}
                value={selectedParticipant}
                onChange={(event, newValue) => {
                  setSelectedParticipant(newValue);
                  setSelectedWallet(null);
                  setError({ ...error, participant: EMPTY_STRING });
                }}
                disabled={Boolean(walletId)}
                loading={userLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Participant"
                    error={!!error.participant}
                    helperText={error.participant}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading ? <CircularProgress size={20} style={{ marginRight: 10 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start"
                    }}
                  >
                    <span>{`${option.firstName} ${option.lastName}`}</span>
                    <span style={{ color: "#64748B" }}>{option.email}</span>
                  </li>
                )}
              />

              {selectedParticipant && (
                <Autocomplete
                  options={wallets?.list}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => option.name || EMPTY_STRING}
                  value={selectedWallet}
                  onChange={(event, newValue) => {
                    setSelectedWallet(newValue);
                    setBalance(newValue?.balance || 0);
                    setError({ ...error, wallet: EMPTY_STRING });
                  }}
                  loading={walletLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Wallet"
                      error={!!error.wallet}
                      helperText={error.wallet}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {walletLoading ? <CircularProgress size={20} style={{ marginRight: 10 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  disabled={Boolean(walletId)}
                />
              )}
            </>
          ) : (
            <>
              <Autocomplete
                options={groups?.list}
                getOptionLabel={(option) => option?.name ?? EMPTY_STRING}
                value={selectedGroup}
                onChange={(event, newValue) => {
                  setSelectedGroup(newValue);
                  setSelectedGroupWallet(null);
                  setError({ ...error, group: EMPTY_STRING });
                }}
                loading={groupsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Group"
                    error={!!error.group}
                    helperText={error.group}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {groupsLoading ? <CircularProgress size={20} style={{ marginRight: 10 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start"
                    }}
                  >
                    <span>{option?.name}</span>
                    <span style={{ color: "#64748B" }}>{option?.description}</span>
                  </li>
                )}
              />

              {selectedGroup && (
                <Autocomplete
                  options={groupDetail?.wallets || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => option.name || EMPTY_STRING}
                  value={selectedGroupWallet}
                  onChange={(event, newValue) => {
                    setSelectedGroupWallet(newValue);
                    setBalance(newValue?.balance || 0);
                    setError({ ...error, groupWallet: EMPTY_STRING });
                  }}
                  loading={groupDetailLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Wallet"
                      error={!!error.groupWallet}
                      helperText={error.groupWallet}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {groupDetailLoading ? <CircularProgress size={20} style={{ marginRight: 10 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              )}
            </>
          )}
          <FormControl fullWidth>
            <InputLabel id="fund-type-label">Fund Type</InputLabel>
            <Select
              labelId="fund-type-label"
              id="fund-type"
              value={fundAction}
              label="Fund Type"
              onChange={handleFundActionChange}
              disabled={Boolean(walletId)}
            >
              <MenuItem value="DEPOSIT">Add Funds</MenuItem>
              <MenuItem value="WITHDRAWAL">Remove Funds</MenuItem>
            </Select>
          </FormControl>
          {((depositMode === "Individual" && selectedWallet && selectedParticipant) ||
            (depositMode === "Group" && selectedGroupWallet && selectedGroup)) && (
            <FormControl fullWidth error={!!error.option}>
              <InputLabel id="payment-reason-label">Payment Reason</InputLabel>
              <Select
                labelId="payment-reason-label"
                id="payment-reason"
                value={selectedOption}
                label="Payment Reason"
                onChange={handleOptionChange}
                inputProps={{ "aria-label": "Payment Reason" }}
              >
                {fundAction === "DEPOSIT"
                  ? addPaymentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.title}
                      </MenuItem>
                    ))
                  : removePaymentTypes.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.title}
                      </MenuItem>
                    ))}
              </Select>
              {error.option && (
                <div className={styles.errorText}>
                  <ErrorOutlineIcon fontSize="small" style={{ marginRight: "4px" }} />
                  {error.option}
                </div>
              )}
            </FormControl>
          )}

          <div className={styles.amountCard}>
            <div className={styles.title}>How much do you need?</div>
            <div className={styles.amount}>
              <div
                className={styles.amtValue}
                onClick={() => {
                  setAmount(10);
                  setError({ ...error, amount: EMPTY_STRING });
                }}
              >
                $10
              </div>
              <div
                className={styles.amtValue}
                onClick={() => {
                  setAmount(20);
                  setError({ ...error, amount: EMPTY_STRING });
                }}
              >
                $20
              </div>
              <div
                className={styles.amtValue}
                onClick={() => {
                  setAmount(50);
                  setError({ ...error, amount: EMPTY_STRING });
                }}
              >
                $50
              </div>
              <div
                className={styles.amtValue}
                onClick={() => {
                  setAmount(100);
                  setError({ ...error, amount: EMPTY_STRING });
                }}
              >
                $100
              </div>
            </div>
            <div className={styles.amountInput}>
              <input
                type="number"
                placeholder="Enter Amount"
                value={amount || ""}
                onChange={(e) => {
                  setAmount(Number(e.target.value));
                  if (Number(e.target.value) > 0) {
                    setError({ ...error, amount: EMPTY_STRING });
                  }
                }}
                className={error.amount ? styles.errorInput : ""}
              />
              {error.amount && (
                <div className={styles.errorText}>
                  <ErrorOutlineIcon fontSize="small" style={{ marginRight: "4px" }} />
                  {error.amount}
                </div>
              )}
            </div>
          </div>

          {((depositMode === "Individual" && selectedParticipant && selectedWallet) ||
            (depositMode === "Group" && selectedGroup && selectedGroupWallet)) && (
            <div className={styles.btn}>
              <Button
                className={fundAction === "DEPOSIT" ? styles.depositBtn : styles.revokeBtn}
                variant="contained"
                onClick={handleTransaction}
                disabled={addFundsLoading}
              >
                {addFundsLoading ? (
                  <CircularProgress size={24} style={{ color: "white" }} />
                ) : fundAction === "DEPOSIT" ? (
                  "Deposit"
                ) : (
                  "Revoke"
                )}
              </Button>
            </div>
          )}
        </div>

        {depositMode === "Individual" && selectedParticipant ? (
          <div className={styles.detailContainer}>
            <div className={styles.userInfo}>
              <h4>Participant information</h4>
              <div className={styles.userdata}>
                <div className={styles.label}>
                  <label>Name : </label>
                  <div className={styles.value}>
                    {selectedParticipant?.firstName + " " + selectedParticipant?.lastName}
                  </div>
                </div>
                <div className={styles.label}>
                  <label>Email : </label>
                  <div className={styles.value}>{selectedParticipant?.email}</div>
                </div>
                <div className={styles.label}>
                  <label>Role : </label>
                  <div className={styles.value}>{roleMapping[selectedParticipant?.currentOrganisationRole]}</div>
                </div>
                <div className={styles.label}>
                  <label>Phone : </label>
                  <div className={styles.value}>{selectedParticipant?.phone}</div>
                </div>
                <div className={styles.label}>
                  <label>User Display Id : </label>
                  <div className={styles.value}>{selectedParticipant?.id}</div>
                </div>
                <div className={styles.label}>
                  <label>User Id : </label>
                  <div className={styles.value}>{selectedParticipant?.userId}</div>
                </div>
              </div>
            </div>
            {selectedWallet && (
              <div className={styles.walletInfo}>
                <h4>Wallet information</h4>
                <div className={styles.walletdata}>
                  <div className={styles.label}>
                    <label>Name : </label>
                    <div className={styles.value}>{selectedWallet?.name}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Email : </label>
                    <div className={styles.value}>{selectedWallet?.participant?.email}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Description : </label>
                    <div className={styles.value}>{selectedWallet?.description}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Balance : </label>
                    <div className={styles.value}>${balance}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Wallet Display Id : </label>
                    <div className={styles.value}>{selectedWallet?.displayId}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Wallet Id : </label>
                    <div className={styles.value}>{selectedWallet?.id}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : depositMode === "Group" && selectedGroup ? (
          <div className={styles.detailContainer}>
            <div className={styles.userInfo}>
              <h4>Group information</h4>
              <div className={styles.userdata}>
                <div className={styles.label}>
                  <label>Name : </label>
                  <div className={styles.value}>{selectedGroup?.name}</div>
                </div>
                <div className={styles.label}>
                  <label>Description : </label>
                  <div className={styles.value}>{selectedGroup?.description}</div>
                </div>
                <div className={styles.label}>
                  <label>Group Id : </label>
                  <div className={styles.value}>{selectedGroup?.group_id}</div>
                </div>
              </div>
            </div>
            {selectedGroupWallet && (
              <div className={styles.walletInfo}>
                <h4>Wallet information</h4>
                <div className={styles.walletdata}>
                  <div className={styles.label}>
                    <label>Name : </label>
                    <div className={styles.value}>{selectedGroupWallet?.name}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Description : </label>
                    <div className={styles.value}>{selectedGroupWallet?.description}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Balance : </label>
                    <div className={styles.value}>${balance}</div>
                  </div>
                  <div className={styles.label}>
                    <label>Wallet Id : </label>
                    <div className={styles.value}>{selectedGroupWallet?.wallet_id}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
